import React, { useState } from 'react';
import { registerTeam } from '../../server/api/hackathon';

interface TeamMember {
  name: string;
  email: string;
  githubUsername?: string;
  linkedinUrl?: string;
}

const HackathonRegistration: React.FC = () => {
  const [formData, setFormData] = useState({
    teamName: '',
    teamSize: '1',
    teamMembers: [
      {
        name: '',
        email: '',
        githubUsername: '',
        linkedinUrl: '',
      }
    ] as TeamMember[]
  });
  const [showThankYou, setShowThankYou] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await registerTeam(formData);
      if (result.success) {
        setShowThankYou(true);
        // Reset form
        setFormData({
          teamName: '',
          teamSize: '1',
          teamMembers: [{
            name: '',
            email: '',
            githubUsername: '',
            linkedinUrl: '',
          }]
        });
      } else {
        throw new Error('Registration failed');
      }
    } catch {
      alert('Failed to register team. Please try again.');
    }
  };

  const handleTeamSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value);
    const currentSize = formData.teamMembers.length;
    
    if (newSize > currentSize) {
      // Add new team members
      const newMembers = Array(newSize - currentSize).fill({
        name: '',
        email: '',
        githubUsername: '',
        linkedinUrl: '',
      });
      setFormData(prev => ({
        ...prev,
        teamSize: e.target.value,
        teamMembers: [...prev.teamMembers, ...newMembers]
      }));
    } else if (newSize < currentSize) {
      // Remove excess team members
      setFormData(prev => ({
        ...prev,
        teamSize: e.target.value,
        teamMembers: prev.teamMembers.slice(0, newSize)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        teamSize: e.target.value
      }));
    }
  };

  const handleTeamMemberChange = (index: number, field: keyof TeamMember, value: string) => {
    setFormData(prev => ({
      ...prev,
      teamMembers: prev.teamMembers.map((member, i) => 
        i === index ? { ...member, [field]: value } : member
      )
    }));
  };

  const handleTeamNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      teamName: e.target.value
    }));
  };

  return (
    <section className="py-16 backdrop-blur-sm">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {showThankYou ? (
          <div className="text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-extrabold pb-2 tracking-tight text-black font-mono">
              Thank You for Registering! 🎉
            </h2>
            <p className="mt-4 text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Your team has been successfully registered for the hackathon.
              <br />
              You are welcome to submit your project now! <a href="/hackathon/submit" className="text-cyan-500 hover:text-cyan-600">Submit Project</a>
            </p>
            <button
              onClick={() => setShowThankYou(false)}
              className="px-8 py-4 bg-white text-slate-900 border-2 border-cyan-500 hover:bg-cyan-50 transition-all duration-300 ease-in-out hover:shadow-lg hover:-translate-y-1 font-medium text-lg rounded-lg"
            >
              Register Another Team
            </button>
          </div>
        ) : (
          <>
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-extrabold pb-2 tracking-tight text-black font-mono">
                Register Here!
              </h2>
              <br/>
              <p className="mt-4 text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
                Join us to build something great and win prizes!
                <br/>
                Fill out the form below to register your team.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-12 space-y-8">
              <div className="space-y-8">
                <div>
                  <label htmlFor="teamName" className="block text-sm font-medium text-slate-700 mb-2">
                    Team Name
                  </label>
                  <input
                    type="text"
                    name="teamName"
                    id="teamName"
                    required
                    value={formData.teamName}
                    onChange={handleTeamNameChange}
                    className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                    placeholder="Enter your team name"
                  />
                </div>

                <div>
                  <label htmlFor="teamSize" className="block text-sm font-medium text-slate-700 mb-2">
                    Number of Team Members
                  </label>
                  <select
                    name="teamSize"
                    id="teamSize"
                    required
                    value={formData.teamSize}
                    onChange={handleTeamSizeChange}
                    className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                  </select>
                </div>

                {formData.teamMembers.map((member, index) => (
                  <div key={index} className="space-y-4 p-6 border border-slate-200 rounded-lg bg-white/50">
                    <h3 className="text-lg font-medium text-slate-900">Team Member {index + 1}</h3>
                    
                    <div>
                      <label htmlFor={`name-${index}`} className="block text-sm font-medium text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id={`name-${index}`}
                        required
                        value={member.name}
                        onChange={(e) => handleTeamMemberChange(index, 'name', e.target.value)}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="Enter full name"
                      />
                    </div>

                    <div>
                      <label htmlFor={`email-${index}`} className="block text-sm font-medium text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id={`email-${index}`}
                        required
                        value={member.email}
                        onChange={(e) => handleTeamMemberChange(index, 'email', e.target.value)}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="Enter email address"
                      />
                    </div>

                    <div>
                      <label htmlFor={`github-${index}`} className="block text-sm font-medium text-slate-700 mb-2">
                        GitHub Username (Optional)
                      </label>
                      <input
                        type="text"
                        id={`github-${index}`}
                        value={member.githubUsername}
                        onChange={(e) => handleTeamMemberChange(index, 'githubUsername', e.target.value)}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="Enter GitHub username"
                      />
                    </div>

                    <div>
                      <label htmlFor={`linkedin-${index}`} className="block text-sm font-medium text-slate-700 mb-2">
                        LinkedIn Profile URL (Optional)
                      </label>
                      <input
                        type="url"
                        id={`linkedin-${index}`}
                        value={member.linkedinUrl}
                        onChange={(e) => handleTeamMemberChange(index, 'linkedinUrl', e.target.value)}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="https://linkedin.com/in/your-profile"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col items-center gap-4 pt-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-200 w-full">
                  <p className="text-slate-600">
                    Like what you see? Consider starring our repository ⭐️
                    <a 
                      href="https://github.com/StructuredLabs/preswald" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 ml-1"
                    >
                      github.com/StructuredLabs/preswald
                    </a>
                  </p>
                </div>
                <button
                  type="submit"
                  className="px-8 py-4 bg-white text-slate-900 border-2 border-cyan-500 hover:bg-cyan-50 transition-all duration-300 ease-in-out hover:shadow-lg hover:-translate-y-1 font-medium text-lg rounded-lg"
                >
                  Register Now
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
};

export default HackathonRegistration; 